import React, { useState } from 'react';

import { 
  Card, 
  CardContent, 
  Divider, 
  Button, 
  CardHeader, 
  InputLabel, 
  FormControl, 
  FilledInput, 
  FormHelperText, 
  InputAdornment 
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import PasswordStrengthBar from 'react-password-strength-bar';
import {useNotification} from 'config/Notification'

import { updatePassword as updatePasswordService} from 'services/modules/idp/auth';

function UpdatePassword() {
  // Notification
  const {openNotification}=useNotification()
  
  // State variables
  const [showPreviousPassword, setShowPreviousPassword] = useState(true);
  const [showProposedPassword, setShowProposedPassword] = useState(true);
  const [showReEnterProposedPassword, setShowReEnterProposedPassword] = useState(true);
  const [errorMessage,setErrorMessage]=useState("");
  const mt={marginTop:'180px'}
  const [formData, setFormData] = useState({
    previousPassword: '',
    proposedPassword: '',
    reEnterProposedPassword: ''
  });
  const [formErrors, setFormErrors] = useState({
    previousPassword: false,
    proposedPassword: false,
    reEnterProposedPassword: false
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // This method toggles the visibility pf password
  const handleToggleVisibility = (field) => {
    switch (field) {
      case 'previousPassword':
        setShowPreviousPassword((prev) => !prev);
        break;
      case 'proposedPassword':
        setShowProposedPassword((prev) => !prev);
        break;
      case 'reEnterProposedPassword':
        setShowReEnterProposedPassword((prev) => !prev);
        break;
      default:
        break;
    }
  };

  // This method returns the password score according to its complexity
  const getPasswordScore = () => {
    const length = formData.proposedPassword.length;
    const hasNumber = /\d/.test(formData.proposedPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.proposedPassword);
    const hasUpperCase = /[A-Z]/.test(formData.proposedPassword);

    if (length >= 8 && hasNumber && hasSpecialChar && hasUpperCase) {
      return 3; // High
    } else if (length >= 8 && (hasNumber || hasSpecialChar || hasUpperCase)) {
      return 2; // Medium
    } else {
      return 1; // Low
    }
  };

  // This method handles change in form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (name === 'reEnterProposedPassword') {
      setPasswordsMatch(value === formData.proposedPassword);
    }
  }

  // This method displays the form error if the form validation is not satisfied
  const handleErrors = (field) => {
    setFormErrors({
      ...formErrors,
      [field]: true
    });
  }
  // This method returns true if form inputs are empty
  const isFieldEmpty = (field) => {
    return formData[field].trim() === '';
  }

  // This method handles the form submission for updating the password
  const handleUpdatePassword= async(e) => {  
    e.preventDefault();  
    const passwordScore = getPasswordScore();
    const emptyFields = Object.entries(formData)
    .filter(([name, value]) => value === '')
    .map(([name]) => name);

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.join(', ');
      openNotification(`Please fill in the required fields: ${fieldNames}`,'error');
      return;
    }
    if(!passwordsMatch)
    return;
    setErrorMessage("");
    if(passwordScore > 1 && passwordsMatch) {
      try
      {
      const response = await updatePasswordService(formData);
      if(response?.status === 200) {
        openNotification("Password changed successfully",'success');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
      else{
        openNotification(response?.data?.message,'error');
      }
    }
    catch(error)
    {
      openNotification(error?.message,'error')
    }
    } else {
      openNotification("Password not strong enough or passwords do not match",'error');
    }
  };

  return (
    <div className="summary-dialog-actions cell-items">
      <div className="summary-dialog-actions cell-items" style={mt}> 
      <Card className="card update-password-card">
        <CardHeader
          title={
            <React.Fragment>
              <div className='card-head'>
                <div className="flex-head">
                  <label className='label'>
                    <LockIcon className="upload-icon" />
                    Update password
                  </label>
                </div>
                <div>
                  <Button variant="contained" className="back font" onClick={()=>window.history.back()}>Back</Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <CardContent>
            <div className="pa-md">
          <FormControl variant="filled" fullWidth>
            <InputLabel htmlFor="filled-adornment-previous-password" className={formErrors.previousPassword && isFieldEmpty('previousPassword') ? 'error-label' : ''}>Previous password</InputLabel>
            <FilledInput
              fullWidth
              type={showPreviousPassword ? 'password' : 'text'}
              name="previousPassword"
              value={formData.previousPassword}
              onChange={handleInputChange}
              onBlur={() => handleErrors('previousPassword')}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleToggleVisibility('previousPassword')}
                    edge="end"
                  >
                    {showPreviousPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              error={formErrors.previousPassword && isFieldEmpty('previousPassword')}
            />
            {formErrors.previousPassword && isFieldEmpty('previousPassword') && (
              <FormHelperText error>Field is required</FormHelperText>
            )}
          </FormControl>
          </div>
          <div className="pa-md">
          <FormControl variant="filled" fullWidth>
            <InputLabel htmlFor="filled-adornment-proposed-password" className={formErrors.proposedPassword && isFieldEmpty('proposedPassword') ? 'error-label' : ''}>New password</InputLabel>
            <FilledInput
              fullWidth
              type={showProposedPassword ? 'password' : 'text'}
              name="proposedPassword"
              value={formData.proposedPassword}
              onChange={handleInputChange}
              onBlur={() => handleErrors('proposedPassword')}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleToggleVisibility('proposedPassword')}
                    edge="end"
                  >
                    {showProposedPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              error={formErrors.proposedPassword && isFieldEmpty('proposedPassword')}
            />
            {formErrors.proposedPassword && isFieldEmpty('proposedPassword') && (
              <FormHelperText error>Field is required</FormHelperText>
            )}
          </FormControl>
          </div>
          <div className="password-strength-bar pa-md">
              <PasswordStrengthBar password={formData.proposedPassword} barColors={['#ccc', '#f44336', '#ff9800', '#1fd655', '#00c04b']} />
            </div>
          <div className="pa-md">
          <FormControl variant="filled" fullWidth>
            <InputLabel htmlFor="filled-adornment-reEnter-proposed-password" className={formErrors.reEnterProposedPassword && isFieldEmpty('reEnterProposedPassword') ? 'error-label' : ''}>Re-enter new password</InputLabel>
            <FilledInput
              fullWidth
              type={showReEnterProposedPassword ? 'password' : 'text'}
              name="reEnterProposedPassword"
              value={formData.reEnterProposedPassword}
              onChange={handleInputChange}
              onBlur={() => handleErrors('reEnterProposedPassword')}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleToggleVisibility('reEnterProposedPassword')}
                    edge="end"
                  >
                    {showReEnterProposedPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              error={formErrors.reEnterProposedPassword && isFieldEmpty('reEnterProposedPassword')}
            />
            {formErrors.reEnterProposedPassword && isFieldEmpty('reEnterProposedPassword') && (
              <FormHelperText error>Field is required</FormHelperText>
            )}
            {!passwordsMatch && (
              <FormHelperText error>Passwords do not match</FormHelperText>
            )}
          </FormControl>
          </div>
          <div className="report-frequency">
          <Button variant="contained" onClick={handleUpdatePassword} >Update password</Button>
          </div>
        </CardContent>
        <p className="error-message">{errorMessage}</p>
      </Card>
      </div>
    </div>
  );
}

export default UpdatePassword;
